// src/pages/Estudantes/VinculosInstitucionaisPage.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  School as SchoolIcon,
  Check as CheckIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

const VinculosInstitucionaisPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Estados
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogCodigoAberto, setDialogCodigoAberto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [verificandoCodigo, setVerificandoCodigo] = useState(false);
  const [codigoValido, setCodigoValido] = useState(null);
  const [dialogDetalhesAberto, setDialogDetalhesAberto] = useState(false);
  const [vinculoSelecionado, setVinculoSelecionado] = useState(null);
  
  // Carregar vínculos do estudante
  useEffect(() => {
    const fetchVinculos = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/vinculos/estudante/${user.id}`);
        setVinculos(response.data);
      } catch (error) {
        console.error('Erro ao carregar vínculos:', error);
        enqueueSnackbar('Erro ao carregar seus vínculos institucionais', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id) {
      fetchVinculos();
    }
  }, [user, enqueueSnackbar]);
  
  // Função para verificar código de convite
  const verificarCodigo = async () => {
    if (!codigo) return;
    
    try {
      setVerificandoCodigo(true);
      const response = await api.get(`/api/convites/verificar/${codigo}`);
      setCodigoValido(response.data);
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      setCodigoValido({
        valido: false,
        mensagem: error.response?.data?.mensagem || 'Código inválido ou expirado'
      });
    } finally {
      setVerificandoCodigo(false);
    }
  };
  
  // Função para usar código de convite
  const usarCodigo = async () => {
    if (!codigoValido?.valido) return;
    
    try {
      setVerificandoCodigo(true);
      
      // Primeiro buscar o ID do estudante associado ao usuário
      const estudanteResponse = await api.get(`/api/estudantes/usuario/${user.id}`);
      const estudanteId = estudanteResponse.data.id;
      
      if (!estudanteId) {
        enqueueSnackbar('Erro: Seu perfil de estudante não foi encontrado', { variant: 'error' });
        return;
      }
      
      // Criar o vínculo usando o código
      const response = await api.post('/api/vinculos', {
        estudanteId,
        instituicaoId: codigoValido.convite.instituicao.id,
        codigoConviteId: codigoValido.convite.id
      });
      
      enqueueSnackbar('Vínculo criado com sucesso!', { variant: 'success' });
      
      // Adicionar o novo vínculo à lista
      setVinculos(prev => [...prev, response.data.vinculo]);
      
      // Resetar estado e fechar diálogo
      setCodigo('');
      setCodigoValido(null);
      setDialogCodigoAberto(false);
    } catch (error) {
      console.error('Erro ao usar código de convite:', error);
      enqueueSnackbar(error.response?.data?.mensagem || 'Erro ao criar vínculo', { variant: 'error' });
    } finally {
      setVerificandoCodigo(false);
    }
  };
  
  // Função para renderizar chip de status
  const renderStatusChip = (status) => {
    let color;
    
    switch (status) {
      case 'ATIVO':
        color = 'success';
        break;
      case 'INATIVO':
        color = 'error';
        break;
      case 'PENDENTE':
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };
  
  // Função para abrir dashboard de instituição
  const abrirDashboardInstituicao = (instituicaoId) => {
    // Verificar recomendação: implementar seleção de instituição no contexto do usuário
    navigate(`/dashboard?instituicaoId=${instituicaoId}`);
  };
  
  // Função para ver detalhes do vínculo
  const verDetalhesVinculo = (vinculo) => {
    setVinculoSelecionado(vinculo);
    setDialogDetalhesAberto(true);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Meus Vínculos Institucionais
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <Alert severity="info">
          <Typography variant="body1">
            Seus vínculos institucionais determinam quais instituições e módulos você pode acessar no sistema. Se você recebeu um código de convite de uma instituição, use o botão abaixo para adicioná-la.
          </Typography>
        </Alert>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogCodigoAberto(true)}
        >
          Usar Código de Convite
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : vinculos.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.neutral' }}>
          <SchoolIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Você ainda não tem vínculos institucionais
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Use um código de convite para se vincular a uma instituição e acessar seus módulos.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setDialogCodigoAberto(true)}
            startIcon={<AddIcon />}
          >
            Adicionar Vínculo
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {vinculos.map((vinculo) => (
            <Grid item xs={12} md={6} lg={4} key={vinculo.id}>
              <Card 
                elevation={3}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderLeft: 4,
                  borderColor: vinculo.status === 'ATIVO' ? 'success.main' : 'grey.300'
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      src={vinculo.instituicao?.logo || '/uploads/default-logo.png'}
                      sx={{ mr: 2, width: 50, height: 50 }}
                    >
                      <SchoolIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" gutterBottom component="div">
                        {vinculo.instituicao?.nome || 'Instituição'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {vinculo.instituicao?.tipoInstituicao || 'Tipo não disponível'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box>
                        {renderStatusChip(vinculo.status)}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Vinculado em
                      </Typography>
                      <Typography variant="body2">
                        {vinculo.dataVinculacao ? 
                          format(new Date(vinculo.dataVinculacao), 'dd/MM/yyyy', { locale: ptBR }) : 
                          '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="caption" color="text.secondary">
                    Tipo de Vínculo
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {vinculo.tipoVinculo || 'REGULAR'}
                  </Typography>
                  
                  {vinculo.modulosAcesso && Array.isArray(vinculo.modulosAcesso) && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Módulos Acessíveis
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {vinculo.modulosAcesso.length > 0 ? (
                          vinculo.modulosAcesso.map(modulo => (
                            <Chip 
                              key={modulo} 
                              label={modulo} 
                              size="small" 
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Todos os módulos disponíveis
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => verDetalhesVinculo(vinculo)}
                    startIcon={<InfoIcon />}
                    fullWidth
                  >
                    Detalhes
                  </Button>
                  
                  {vinculo.status === 'ATIVO' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => abrirDashboardInstituicao(vinculo.instituicaoId)}
                      startIcon={<LinkIcon />}
                      fullWidth
                    >
                      Acessar
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Diálogo para adicionar código de convite */}
      <Dialog 
        open={dialogCodigoAberto} 
        onClose={() => {
          setDialogCodigoAberto(false);
          setCodigo('');
          setCodigoValido(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Adicionar Vínculo Institucional
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Digite o código de convite fornecido pela instituição:
          </Typography>
          
          <TextField
            fullWidth
            label="Código de Convite"
            value={codigo}
            onChange={(e) => {
              setCodigo(e.target.value);
              setCodigoValido(null);
            }}
            variant="outlined"
            margin="normal"
            disabled={verificandoCodigo}
            error={codigoValido && !codigoValido.valido}
            helperText={codigoValido && (codigoValido.valido 
              ? `Código válido para ${codigoValido.convite.instituicao.nome}`
              : codigoValido.mensagem
            )}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={verificarCodigo}
              disabled={!codigo || verificandoCodigo}
              startIcon={verificandoCodigo ? <CircularProgress size={20} /> : null}
            >
              Verificar Código
            </Button>
          </Box>
          
          {codigoValido && codigoValido.valido && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success">
                <Typography variant="body1" gutterBottom>
                  Código válido! Você será vinculado a:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Avatar sx={{ mr: 2 }}>
                    <SchoolIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {codigoValido.convite.instituicao.nome}
                    </Typography>
                    <Typography variant="body2">
                      {codigoValido.convite.instituicao.tipoInstituicao}
                    </Typography>
                  </Box>
                </Box>
                
                {codigoValido.convite.modulosAcesso && Array.isArray(codigoValido.convite.modulosAcesso) && codigoValido.convite.modulosAcesso.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Módulos que você terá acesso:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {codigoValido.convite.modulosAcesso.map(modulo => (
                        <Chip 
                          key={modulo} 
                          label={modulo} 
                          size="small" 
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDialogCodigoAberto(false);
              setCodigo('');
              setCodigoValido(null);
            }}
            disabled={verificandoCodigo}
          >
            Cancelar
          </Button>
          <Button 
            onClick={usarCodigo} 
            variant="contained" 
            disabled={verificandoCodigo || !codigoValido || !codigoValido.valido}
            startIcon={verificandoCodigo ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Confirmar Vínculo
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo para ver detalhes do vínculo */}
      <Dialog 
        open={dialogDetalhesAberto} 
        onClose={() => {
          setDialogDetalhesAberto(false);
          setVinculoSelecionado(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {vinculoSelecionado && (
          <>
            <DialogTitle>
              Detalhes do Vínculo Institucional
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  src={vinculoSelecionado.instituicao?.logo || '/uploads/default-logo.png'}
                  sx={{ mr: 2, width: 60, height: 60 }}
                >
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {vinculoSelecionado.instituicao?.nome || 'Instituição'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vinculoSelecionado.instituicao?.tipoInstituicao || 'Tipo não disponível'}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Informações do Vínculo
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                        secondary={renderStatusChip(vinculoSelecionado.status)} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Data de Vinculação" 
                        secondary={
                          vinculoSelecionado.dataVinculacao ? 
                            format(new Date(vinculoSelecionado.dataVinculacao), 'dd/MM/yyyy', { locale: ptBR }) : 
                            'Data não disponível'
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Tipo de Vínculo" 
                        secondary={vinculoSelecionado.tipoVinculo || 'REGULAR'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Origem do Vínculo" 
                        secondary={vinculoSelecionado.origemVinculo || 'CONVITE'} 
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Módulos Acessíveis
                  </Typography>
                  
                  {vinculoSelecionado.modulosAcesso && Array.isArray(vinculoSelecionado.modulosAcesso) && vinculoSelecionado.modulosAcesso.length > 0 ? (
                    <List>
                      {vinculoSelecionado.modulosAcesso.map(modulo => (
                        <ListItem key={modulo}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              <CheckIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={modulo} 
                            secondary="Acesso concedido" 
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Você tem acesso a todos os módulos disponíveis para esta instituição.
                      </Typography>
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  setDialogDetalhesAberto(false);
                  setVinculoSelecionado(null);
                }}
              >
                Fechar
              </Button>
              {vinculoSelecionado.status === 'ATIVO' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    abrirDashboardInstituicao(vinculoSelecionado.instituicaoId);
                    setDialogDetalhesAberto(false);
                  }}
                  startIcon={<LinkIcon />}
                >
                  Acessar Instituição
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default VinculosInstitucionaisPage;